import { CalendarDays } from 'lucide-react';
import { BackButton } from '@/components/common/BackButton';
import { PersonBadge } from '@/components/common/PersonBadge';
import { SelectedFilterChip } from '@/components/common/SelectedFilterChip';
import { PageTitle } from '@/components/layout/PageTitle';
import { PageToolbar } from '@/components/layout/PageToolbar';
import type { MapOn } from '../constants/moTravel.constants';
import type { MoTravelPeriodParams } from '../types/moTravel.types';
import { getBangladeshToday } from '../utils/bangladeshClock';
import { formatShortDay } from '../utils/dayLabels';
import { getDayRouteColor } from '../utils/dayRoutes';
import { MapOnSwitch } from './MapOnSwitch';
import { MoPeriodDatePicker, type PeriodJourney } from './MoPeriodDatePicker';

const MO_TRAVEL_DETAIL_TITLE = 'MO travel details';
const PERSON_ROLE_LABEL = 'MO';

const PERIOD_NOUN: Record<MapOn, string> = {
  DAILY: 'day',
  WEEKLY: 'week',
  MONTHLY: 'month',
};

interface MoTravelDetailHeaderProps {
  personName: string | undefined;
  isPersonLoading: boolean;
  period: MoTravelPeriodParams;
  journey: PeriodJourney;
  statsDate: string | null;
  onPeriodChange: (periodChange: Partial<MoTravelPeriodParams>) => void;
  onShowWholePeriod: () => void;
  onBack: () => void;
}

export function MoTravelDetailHeader({
  personName,
  isPersonLoading,
  period,
  journey,
  statsDate,
  onPeriodChange,
  onShowWholePeriod,
  onBack,
}: MoTravelDetailHeaderProps) {
  const selectedDayChip = statsDate ? (
    <SelectedFilterChip
      icon={CalendarDays}
      label="Only"
      value={formatShortDay(statsDate)}
      clearLabel={`Show the whole ${PERIOD_NOUN[period.mapOn]}`}
      onClear={onShowWholePeriod}
      size="sm"
      tintColor={getDayRouteColor(statsDate)}
    />
  ) : null;

  return (
    <div className="flex flex-col gap-3">
      <PageTitle leading={<BackButton onClick={onBack} />} titleAddon={selectedDayChip}>
        {MO_TRAVEL_DETAIL_TITLE}
      </PageTitle>
      <PageToolbar
        currentTitle={MO_TRAVEL_DETAIL_TITLE}
        breadcrumbAddon={
          <PersonBadge
            roleLabel={PERSON_ROLE_LABEL}
            name={personName}
            isLoading={isPersonLoading}
          />
        }
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          <MapOnSwitch value={period.mapOn} onChange={(mapOn) => onPeriodChange({ mapOn })} />
          <MoPeriodDatePicker
            date={period.date}
            mapOn={period.mapOn}
            maxDate={getBangladeshToday()}
            journey={journey}
            onDateChange={(date) => onPeriodChange({ date })}
            className="sm:min-w-48 sm:shrink-0"
          />
        </div>
      </PageToolbar>
    </div>
  );
}
